/**
 * Task 17 — compound-cursor pull + conflict handling (docs/08 §5–§8).
 * Never advances the cursor to the client clock. Append-only duplicates are bugs.
 */

import type { CardZappDB } from '../db/database';
import { APPEND_ONLY_TABLES } from '../repositories/entityRegistry';
import type { RemoteRepository } from '../repositories/remoteRepository';
import type { SyncConflict, SyncedTableName } from '../schemas/local-only';
import { advanceCursorFromRow, initialSyncState, PULL_PAGE_SIZE } from './cursor';
import { FLUSH_TABLE_ORDER } from './flushOrder';

export type PullTablesResult = {
  pulled: number;
  inserted: number;
  overwritten: number;
  keptDirty: number;
  conflicts: number;
  /** Append-only id already present locally — logged, not merged (docs/08 §8). */
  appendOnlyDuplicates: number;
};

export type PullTablesArgs = {
  db: CardZappDB;
  remote: RemoteRepository;
  nowIso: string;
  /** Defaults to every synced table in FK-safe order. */
  tables?: readonly SyncedTableName[];
  pageSize?: number;
};

type LocalSyncRow = Record<string, unknown> & {
  id: string;
  _localDirty?: boolean;
  _baseServerUpdatedAt?: string | null;
};

function remoteToLocal(remote: Record<string, unknown>): LocalSyncRow {
  const id = remote['id'];
  if (typeof id !== 'string') {
    throw new Error('pull: remote row missing string id');
  }
  const updatedAt = remote['updated_at'];
  if (typeof updatedAt !== 'string') {
    throw new Error('pull: remote row missing updated_at');
  }
  return {
    ...remote,
    id,
    _localDirty: false,
    _baseServerUpdatedAt: updatedAt,
  };
}

/**
 * Pull one table to completion, paging at `pageSize` (docs/08 §5).
 * Conflict rules: docs/08 §7. Append-only: docs/08 §8.
 */
export async function pullTable(
  db: CardZappDB,
  remote: RemoteRepository,
  table: SyncedTableName,
  nowIso: string,
  pageSize: number = PULL_PAGE_SIZE,
): Promise<PullTablesResult> {
  let cursor = (await db.sync_state.get(table)) ?? initialSyncState(table);
  const result: PullTablesResult = {
    pulled: 0,
    inserted: 0,
    overwritten: 0,
    keptDirty: 0,
    conflicts: 0,
    appendOnlyDuplicates: 0,
  };

  const isAppendOnly = APPEND_ONLY_TABLES.has(table);
  const tableStore = db.table(table);

  for (;;) {
    const page = await remote.selectSince(
      table,
      { lastUpdatedAt: cursor.lastUpdatedAt, lastId: cursor.lastId },
      pageSize,
    );
    if (page.length === 0) break;

    for (const remoteRow of page) {
      result.pulled += 1;
      const id = remoteRow['id'];
      if (typeof id !== 'string') {
        throw new Error(`pull: ${table} row missing id`);
      }

      const local = (await tableStore.get(id)) as LocalSyncRow | undefined;

      if (local === undefined) {
        await tableStore.put(remoteToLocal(remoteRow));
        result.inserted += 1;
      } else if (isAppendOnly) {
        // docs/08 §8 — insert-only; duplicate id is a bug, not a merge.
        result.appendOnlyDuplicates += 1;
      } else if (local._localDirty !== true) {
        await tableStore.put(remoteToLocal(remoteRow));
        result.overwritten += 1;
      } else if (remoteRow['updated_at'] === local._baseServerUpdatedAt) {
        // Server unchanged since our base; keep the local edit for outbox push.
        result.keptDirty += 1;
      } else {
        const conflict: SyncConflict = {
          id: crypto.randomUUID(),
          table,
          rowId: id,
          localRow: local,
          serverRow: remoteRow,
          createdAt: nowIso,
        };
        await db.sync_conflicts.put(conflict);
        result.conflicts += 1;
      }
    }

    const last = page[page.length - 1];
    if (last === undefined) break;
    cursor = advanceCursorFromRow(table, last);
    await db.sync_state.put(cursor);

    if (page.length < pageSize) break;
  }

  return result;
}

/** Pull all synced tables (or a subset). Cursor never uses the client clock. */
export async function pullTables(args: PullTablesArgs): Promise<PullTablesResult> {
  const tables = args.tables ?? FLUSH_TABLE_ORDER;
  const pageSize = args.pageSize ?? PULL_PAGE_SIZE;
  const totals: PullTablesResult = {
    pulled: 0,
    inserted: 0,
    overwritten: 0,
    keptDirty: 0,
    conflicts: 0,
    appendOnlyDuplicates: 0,
  };

  for (const table of tables) {
    const part = await pullTable(args.db, args.remote, table, args.nowIso, pageSize);
    totals.pulled += part.pulled;
    totals.inserted += part.inserted;
    totals.overwritten += part.overwritten;
    totals.keptDirty += part.keptDirty;
    totals.conflicts += part.conflicts;
    totals.appendOnlyDuplicates += part.appendOnlyDuplicates;
  }

  return totals;
}
